<template>
  <RvRentalsPage
    :location="{
      destinationType: POINT_OF_INTEREST_TYPE.World,
      coordinates: geolocation?.latitude && geolocation?.longitude ? {
        latitude: geolocation.latitude,
        longitude: geolocation.longitude,
      } : undefined,
      titleWithPreposition: t('nearYou'),
      titleNearby: t('nearYou'),
    }"
    :content-blocks="data?.contentBlocks"
    :faqs="data?.faqs?.map((faq) => ({
      Id: faq.Id,
      Question: faq.Question ?? '',
      Answer: faq.Answer ?? '',
      Source: faq.Source ?? undefined,
    }))"
    :available-keywords="data?.keywords"
  >
    <template #nearby-locations>
      <TContainer class="my-24">
        <GeneralNearbyLocations
          v-if="data?.nearbyUS?.length"
          :locations="data.nearbyUS"
          kind="RVRentals"
          variant="state"
          show-rv-count
        />
        <GeneralNearbyLocations
          v-if="data?.nearbyCA?.length"
          :locations="data.nearbyCA"
          kind="RVRentals"
          variant="province"
          show-rv-count
        />
      </TContainer>
    </template>
  </RvRentalsPage>
</template>

<script setup lang="ts">
import type { SupportedLocales } from '#shared/types/locales'
import { POINT_OF_INTEREST_TYPE } from '~/constants/point-of-interest'

definePageMeta({
  middleware: [
    'keyword',
  ],
  layout: 'static',
  headerSearchFormVisibility: 'dynamic',

  i18n: {
    paths: {
      en: '/rv-rentals/[keyword]',
      fr: '/locations-vr/[keyword]',
    },
  },
})

const runtimeConfig = useRuntimeConfig()
const { availableLocales, t, te, locale } = useI18n()
const route = useRoute()
const { geolocation } = useGeolocation()
const { getContentBlocks, getRouteDetails, getNearbyRegions } = usePointOfInterest()

const keyword = computed(() => Array.isArray(route.params.keyword) ? route.params.keyword.join(' ') : route.params.keyword)

const { data } = await useAsyncData(`rv-rentals-keyword-${keyword.value}-${locale.value}`, async () => {
  const [contentBlocks, routeDetails, { nearbyUS, nearbyCA }] = await Promise.all([
    getContentBlocks({
      PointOfInterestId: runtimeConfig.public.POIWorldId,
      Keyword: keyword.value,
    }),
    getRouteDetails(),
    getNearbyRegions(),
  ])

  return {
    contentBlocks,
    faqs: routeDetails?.FAQs ?? undefined,
    isIndexable: routeDetails?.PointOfInterest?.Indexable ?? false,
    keywords: routeDetails?.PointOfInterest?.KeywordSlugs ?? [],
    nearbyUS,
    nearbyCA,
  }
})

if (isMetaServer && !data.value?.isIndexable) {
  useSeoMeta({
    robots: 'noindex',
  })
}

/**
 * Set lang switcher value
 */
const setI18nParams = useSetI18nParams()
const i18nParams: Record<SupportedLocales, Record<'keyword', string> | undefined> = { en: undefined, fr: undefined }
availableLocales.forEach((localeCode) => {
  let localizedKeyword = ''
  if (keyword.value) {
    localizedKeyword = te(`keyword.${keyword.value}.slug.${localeCode}`)
      ? t(`keyword.${keyword.value}.slug.${localeCode}`)
      : keyword.value
  }

  i18nParams[localeCode] = {
    keyword: localizedKeyword,
  }
})

setI18nParams(i18nParams)
</script>

<i18n src="~~/i18n/locales/common/search/keywords.json" lang="json" />

<i18n src="~~/i18n/locales/common/nearby-locations.json" lang="json" />
